/**
 * Shared date formatting for partner-facing views.
 *
 * Both formats below already existed privately in five components
 * (`fmtMonthYear` in DateRangeFilter, `formatDate` in UsersTable /
 * UserDrillDownPanel / CompanyOverviewTable) — this is the one place they
 * should come from. Those copies are worth folding in here, but not while
 * `CompanyOverviewTable` is open in another PR.
 *
 * Every helper is null-safe and returns an em dash for missing or unparseable
 * input, so callers can hand over an API field directly without guarding: a
 * table cell showing "—" is right, one showing "Invalid Date" is not.
 */

import { format } from 'date-fns';

/** What a field renders as when there is nothing to show. */
export const EM_DASH = '—';

/** `YYYY-MM-DD` with no time part — a calendar date, not an instant. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const parse = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  // `new Date("2026-12-01")` is parsed as UTC midnight, so in any timezone
  // behind UTC it formats as the *previous* day — "December 2026" becomes
  // "November 2026". A date carrying no time is a calendar date, so build it
  // from local components and let it stay the day it says it is. Timestamps
  // (which do carry a zone) are parsed normally.
  const dateOnly = DATE_ONLY.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `"May 2026"` — for coarse dates like "Member Since". */
export const formatMonthYear = (value: string | null | undefined): string => {
  const date = parse(value);
  if (!date) return EM_DASH;
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/** `"Aug 4, 2026"` — the default for a specific day (joined, last active). */
export const formatDayMonthYear = (value: string | null | undefined): string => {
  const date = parse(value);
  if (!date) return EM_DASH;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** `"Jun 15"` — FR-004's "Invited on {Month} {day}", always month + day, no year. */
export const formatMonthDay = (value: string | null | undefined): string => {
  const date = parse(value);
  if (!date) return EM_DASH;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * `"Today"` / `"May 25"` / `"May 25, 2025"` — the Last Active column.
 *
 * Matches the Figma table (703:2621), which shows a bare day-and-month for the
 * current year and reserves the year for anything older. "Today" is spelled out
 * because a date is the one thing a reader has to translate; the most common
 * answer should not need translating.
 */
export const formatLastActive = (value: string | null | undefined): string => {
  const date = parse(value);
  if (!date) return EM_DASH;

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) return 'Today';

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return formatDayMonthYear(value);
};

/**
 * `"Jun 24, 2024 at 12:53 PM"` — API Keys' Created column
 * (Figma 2860:13126). Unlike the rest of this file this column carries a
 * time, not just a day, so the existing day-only helpers don't fit; kept
 * here rather than inlined in the page so the exact "at" wording (matching
 * the design copy, not `toLocaleString`'s default comma) stays in one place.
 */
export const formatDateTime = (value: string | null | undefined): string => {
  const date = parse(value);
  if (!date) return EM_DASH;

  // date-fns tokens: MMM/d/yyyy match the day-only helpers above (short
  // month, no leading zero on the day); h/mm/a is a 12-hour clock with
  // zero-padded minutes — together the same shape as the previous
  // toLocaleDateString/toLocaleTimeString pair, just joined with a literal
  // "'at'" instead of assembling the two strings by hand.
  return format(date, "MMM d, yyyy 'at' h:mm a");
};
